import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TableColumn, TableConfig, SortConfig, PaginationConfig } from '../../models/table.interface';

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './data-table.html',
  styleUrls: ['./data-table.scss']
})
export class DataTableComponent<T = any> implements OnInit, OnChanges {
  @Input() columns: TableColumn<T>[] = [];
  @Input() data: T[] = [];
  @Input() config: TableConfig = {
    searchable: true,
    sortable: true,
    paginated: true,
    itemsPerPage: 10,
    striped: false,
    hover: true,
    bordered: false,
    responsive: true
  };
  @Input() isMobile: boolean = false;
  @Input() mobileSeparateColumn: string = 'balance';
  @Input() loading: boolean = false;

  @Output() rowClick = new EventEmitter<T>();
  @Output() actionClick = new EventEmitter<{ action: string; row: T }>();

  // Expose Math for template
  Math = Math;

  // State
  filteredData: T[] = [];
  paginatedData: T[] = [];
  searchTerm: string = '';
  sortConfig: SortConfig = {
    column: null,
    direction: 'asc'
  };

  // Sort dropdown state
  showSortDropdown = false;

  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  };

  constructor(private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.initializeTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['columns']) {
      this.initializeTable();
    }
  }

  initializeTable(): void {
    this.paginationConfig.itemsPerPage = this.config.itemsPerPage || 10;
    this.paginationConfig.currentPage = 1; // Reset to page 1 on data change
    this.applyFilters();
  }

  /**
   * Search functionality
   */
  onSearch(term: string): void {
    this.searchTerm = term;
    this.paginationConfig.currentPage = 1;
    this.applyFilters();
  }

  /**
   * Apply filters and update data
   */
  applyFilters(): void {
    let filtered = [...this.data];

    // Apply search
    if (this.searchTerm && this.config.searchable) {
      filtered = filtered.filter(row => {
        return this.columns.some(col => {
          const value = (row as any)[col.key];
          if (value === null || value === undefined) return false;
          return value.toString().toLowerCase().includes(this.searchTerm.toLowerCase());
        });
      });
    }

    this.filteredData = filtered;
    this.paginationConfig.totalItems = filtered.length;
    this.paginationConfig.totalPages = Math.ceil(
      filtered.length / this.paginationConfig.itemsPerPage
    );

    this.updatePaginatedData();
  }

  /**
   * Sort functionality
   */
  onSort(column: TableColumn<T>): void {
    if (!this.config.sortable || column.sortable === false) return;

    if (this.sortConfig.column === column.key) {
      this.sortConfig.direction = this.sortConfig.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortConfig.column = column.key;
      this.sortConfig.direction = 'asc';
    }

    this.filteredData.sort((a: any, b: any) => {
      let aVal = a[column.key];
      let bVal = b[column.key];

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return this.sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return this.sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    this.updatePaginatedData();
  }

  /**
   * Get sort class for column header
   */
  getSortClass(column: TableColumn<T>): string {
    if (!this.config.sortable || column.sortable === false) return '';
    if (this.sortConfig.column !== column.key) return 'sortable';
    return `sortable sort-${this.sortConfig.direction}`;
  }

  /**
   * Toggle sort dropdown
   */
  toggleSortDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showSortDropdown = !this.showSortDropdown;
  }

  /**
   * Close sort dropdown
   */
  closeSortDropdown(): void {
    this.showSortDropdown = false;
  }

  /**
   * Check if column is currently sorted
   */
  isSorted(columnKey: string): boolean {
    return this.sortConfig.column === columnKey;
  }

  /**
   * Get sort direction for a column
   */
  getSortDirection(columnKey: string): 'asc' | 'desc' | null {
    return this.isSorted(columnKey) ? this.sortConfig.direction : null;
  }

  /**
   * Handle column sort by key (for mobile dropdown)
   */
  onSortByKey(columnKey: string): void {
    const column = this.columns.find(col => col.key === columnKey);
    if (column) {
      this.onSort(column);
      this.closeSortDropdown();
    }
  }

  /**
   * Pagination functionality
   */
  updatePaginatedData(): void {
    if (!this.config.paginated) {
      this.paginatedData = this.filteredData;
      return;
    }

    const start = (this.paginationConfig.currentPage - 1) * this.paginationConfig.itemsPerPage;
    const end = start + this.paginationConfig.itemsPerPage;
    this.paginatedData = this.filteredData.slice(start, end);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.paginationConfig.totalPages) return;
    this.paginationConfig.currentPage = page;
    this.updatePaginatedData();
  }

  previousPage(): void {
    this.goToPage(this.paginationConfig.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.paginationConfig.currentPage + 1);
  }

  /**
   * Get page numbers for pagination display
   */
  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const total = this.paginationConfig.totalPages;
    const current = this.paginationConfig.currentPage;

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - 1 && i <= current + 1)
      ) {
        pages.push(i);
      } else if (i === current - 2 || i === current + 2) {
        pages.push('...');
      }
    }

    return pages.filter((page, index, arr) => {
      if (page === '...') {
        return arr[index - 1] !== '...';
      }
      return true;
    });
  }

  /**
   * Get table classes
   */
  getTableClasses(): string {
    const classes = ['table', 'data-table'];
    if (this.config.striped) classes.push('table-striped');
    // Only add hover if there's data available
    if (this.config.hover && this.paginatedData.length > 0) classes.push('table-hover');
    if (this.config.bordered) classes.push('table-bordered');
    return classes.join(' ');
  }

  /**
   * Render cell content
   */
  getCellContent(column: TableColumn<T>, row: T): SafeHtml {
    const value = (row as any)[column.key];
    let content: string;

    if (column.render) {
      content = column.render(value, row);
    } else {
      content = value !== null && value !== undefined ? value.toString() : '';
    }

    // Bypass security and trust the HTML content (including inline styles)
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  /**
   * Handle row click
   */
  onRowClick(row: T): void {
    this.rowClick.emit(row);
  }

  /**
   * Export to CSV
   */
  exportToCSV(filename: string = 'export.csv'): void {
    const headers = this.columns.map(col => col.label).join(',');
    const rows = this.filteredData.map((row: any) => {
      return this.columns.map(col => {
        const value = row[col.key];
        return `"${value !== null && value !== undefined ? value : ''}"`;
      }).join(',');
    }).join('\n');

    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Refresh table data
   */
  refresh(): void {
    this.applyFilters();
  }

  /**
   * Clear search and filters
   */
  clearSearch(): void {
    this.searchTerm = '';
    this.paginationConfig.currentPage = 1;
    this.applyFilters();
  }

  /**
   * Get column by key
   */
  getColumn(key: string): TableColumn<T> | undefined {
    return this.columns.find(col => col.key === key);
  }

  /**
   * Check if column exists
   */
  hasColumn(key: string): boolean {
    return this.columns.some(col => col.key === key);
  }
}
